import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ConcentricRings } from '@/components/Motifs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { ExternalLink } from 'lucide-react';
import api from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  })
};

const CATEGORY_ORDER = ['featured', 'offers', 'content', 'tools', 'social', 'general'];
const CATEGORY_LABELS = {
  featured: 'Featured',
  offers: 'Current Offers',
  content: 'Content',
  tools: 'Tools',
  social: 'Connect',
  general: 'Links'
};

export default function LinksPage() {
  usePageTitle('Links');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/links').then(res => {
      setLinks(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleClick = async (link) => {
    try {
      await api.post(`/links/${link._id}/click`);
    } catch {
      // Click tracking is fire-and-forget — navigation proceeds regardless
    }
  };

  // Group links by category
  const grouped = {};
  links.forEach(l => {
    const cat = l.category || 'general';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(l);
  });
  const sortedCategories = CATEGORY_ORDER.filter(c => grouped[c]?.length > 0);
  // Add any categories not in the order
  Object.keys(grouped).forEach(c => {
    if (!sortedCategories.includes(c)) sortedCategories.push(c);
  });

  return (
    <div className="min-h-screen bg-[hsl(164,28%,14%)]">
      {/* Minimal header — no full site nav, just brand */}
      <div className="pt-10 pb-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex justify-center mb-4">
            <ConcentricRings size={48} opacity={0.2} />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))] mb-1">
            The Cosmic Synthesist
          </h1>
          <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
            Consciousness, structured.
          </p>
        </motion.div>
      </div>

      {/* Links */}
      <div className="max-w-md mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <ConcentricRings size={40} opacity={0.3} />
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">No links available yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {sortedCategories.map(cat => (
              <div key={cat}>
                <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--silver))] mb-3 px-1">
                  {CATEGORY_LABELS[cat] || cat}
                </p>
                <div className="space-y-2.5">
                  {grouped[cat].map((link, i) => (
                    <motion.a
                      key={link._id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleClick(link)}
                      initial="hidden"
                      animate="visible"
                      custom={i}
                      variants={fadeUp}
                      className="group flex items-center gap-4 w-full rounded-xl border border-[rgba(201,168,76,0.15)] bg-[hsl(var(--card))] px-5 py-4 transition-all duration-300 hover:border-[rgba(201,168,76,0.35)] hover:bg-[rgba(27,77,68,0.12)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.3)]"
                    >
                      {link.icon && (
                        <span className="text-lg shrink-0 w-6 text-center">{link.icon}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-['Inter'] text-sm font-medium text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--gold))] transition-colors">
                          {link.title}
                        </p>
                        {link.description && (
                          <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))] mt-0.5 truncate">
                            {link.description}
                          </p>
                        )}
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-[hsl(var(--silver))] group-hover:text-[hsl(var(--gold))] transition-colors shrink-0" />
                    </motion.a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <a
            href="/"
            className="font-['Inter'] text-xs text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            thecosmicsynthesist.com
          </a>
        </div>
      </div>
    </div>
  );
}
